import { useMemo, useState } from 'react';
import {
  Card,
  CardHeader,
  Button,
  Badge,
  Input,
  Textarea,
  Modal,
  LoadingSkeleton,
  useToast,
} from '@nexora/design-system';
import {
  useServiceOrder,
  useAddServiceOrderTimeEntry,
  SERVICE_ORDER_STATUS_LABEL,
  SERVICE_ORDER_STATUS_VARIANT,
  formatHours,
} from './useServiceOrders';
import { ServiceOrderPicker } from './ServiceOrderPicker';
import { formatDateTime } from '../../shared/utils/format';

interface ServiceOrderTimeEntriesPageProps {
  /** Id da OS. Se ausente, le `?os=` da URL; sem isso, mostra o seletor. */
  orderId?: string;
}

/**
 * Apontamento de horas de uma Ordem de Servico.
 *
 * Consome:
 *   GET  /manufacturing/service-orders/:id  (agregado — lista de timeEntries)
 *   POST /manufacturing/service-orders/:id/time-entries
 *        body: { employeeName, hours, occurredAt?, description? }
 *        resp: { timeEntryId, totalHours }
 */
export function ServiceOrderTimeEntriesPage({
  orderId,
}: ServiceOrderTimeEntriesPageProps): JSX.Element {
  const { addToast } = useToast();

  const urlOrderId = useMemo(
    () => new URLSearchParams(window.location.search).get('os') ?? undefined,
    [],
  );
  const [pickedId, setPickedId] = useState<string | null>(null);
  const activeId = orderId ?? pickedId ?? urlOrderId;

  const { data: detail, isLoading, error, refetch } = useServiceOrder(activeId);

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ employeeName: '', hours: '', occurredAt: '', description: '' });
  const addTimeEntry = useAddServiceOrderTimeEntry();

  const entries = detail?.timeEntries ?? [];
  const sumHours = useMemo(() => entries.reduce((a, t) => a + t.hours, 0), [entries]);

  if (!activeId) {
    return (
      <div className="so-page">
        <ServiceOrderPicker onSelect={setPickedId} label="Apontamento de Horas" />
      </div>
    );
  }

  const resetForm = (): void => {
    setForm({ employeeName: '', hours: '', occurredAt: '', description: '' });
  };

  const handleSubmit = async (): Promise<void> => {
    const hours = Number(form.hours);
    if (!form.employeeName.trim() || !form.hours || !Number.isFinite(hours) || hours <= 0) {
      addToast('Informe o colaborador e as horas apontadas (maior que zero)', 'error');
      return;
    }
    try {
      const result = await addTimeEntry.mutateAsync({
        id: activeId,
        employeeName: form.employeeName.trim(),
        hours,
        occurredAt: form.occurredAt ? new Date(form.occurredAt).toISOString() : undefined,
        description: form.description.trim() || undefined,
      });
      addToast(`Apontamento registrado. Total da OS: ${formatHours(result.totalHours)}`, 'success');
      setModalOpen(false);
      resetForm();
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Erro ao registrar apontamento', 'error');
    }
  };

  return (
    <div className="so-page">
      {/* ── Header ── */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <div className="flex items-center gap-sm mb-xs flex-wrap">
            <span
              className="font-label-md text-label-md px-2 py-1 rounded"
              style={{ background: '#eceef0', color: '#43474e' }}
            >
              {detail?.number ?? 'Carregando...'}
            </span>
            {detail && (
              <Badge variant={SERVICE_ORDER_STATUS_VARIANT[detail.status]} size="sm">
                {SERVICE_ORDER_STATUS_LABEL[detail.status]}
              </Badge>
            )}
          </div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Apontamento de Horas</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            {detail?.title ? `Serviço: ${detail.title}` : 'Registro de horas trabalhadas na OS.'}
            {detail?.customerRef ? ` · Cliente: ${detail.customerRef}` : ''}
          </p>
        </div>
        <Button onClick={() => { resetForm(); setModalOpen(true); }}>+ Novo Apontamento</Button>
      </div>

      {/* ── KPIs ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-md mb-lg">
        <div className="so-kpi">
          <div className="so-kpi__top">
            <span className="material-symbols-outlined" aria-hidden="true">schedule</span>
            <span className="font-label-md text-label-md text-on-surface-variant uppercase">Horas totais da OS</span>
          </div>
          <div className="font-display-lg text-display-lg text-on-surface tabular-nums">
            {detail ? formatHours(detail.totalHours) : '—'}
          </div>
          <p className="font-body-sm text-body-sm text-on-surface-variant m-0">Soma oficial (servidor)</p>
        </div>
        <div className="so-kpi">
          <div className="so-kpi__top">
            <span className="material-symbols-outlined" aria-hidden="true">groups</span>
            <span className="font-label-md text-label-md text-on-surface-variant uppercase">Apontamentos</span>
          </div>
          <div className="font-display-lg text-display-lg text-on-surface tabular-nums">{entries.length}</div>
          <p className="font-body-sm text-body-sm text-on-surface-variant m-0">Registros nesta OS</p>
        </div>
        <div className="so-kpi">
          <div className="so-kpi__top">
            <span className="material-symbols-outlined" aria-hidden="true">summarize</span>
            <span className="font-label-md text-label-md text-on-surface-variant uppercase">Soma exibida</span>
          </div>
          <div className="font-display-lg text-display-lg text-on-surface tabular-nums">{formatHours(sumHours)}</div>
          <p className="font-body-sm text-body-sm text-on-surface-variant m-0">Soma dos registros listados</p>
        </div>
      </div>

      {/* ── Loading / Error / Empty / Table ── */}
      {isLoading && (
        <Card padding="1rem">
          <LoadingSkeleton count={6} height="48px" />
        </Card>
      )}

      {error && !isLoading && (
        <Card padding="2rem">
          <div className="state-error" role="alert">
            <p>Erro ao carregar a ordem de serviço</p>
            <p className="state-detail">{(error as Error).message}</p>
            <Button size="sm" variant="outline" onClick={() => refetch()}>Tentar novamente</Button>
          </div>
        </Card>
      )}

      {detail && !isLoading && !error && (
        <Card padding="0">
          <CardHeader
            className="px-md pt-md"
            action={
              <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
                {entries.length} {entries.length === 1 ? 'apontamento' : 'apontamentos'}
              </span>
            }
          >
            <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Horas apontadas</h3>
          </CardHeader>
          <div className="so-table-wrap">
            <table className="so-table">
              <thead>
                <tr>
                  <th style={{ width: '40%' }}>Colaborador</th>
                  <th style={{ textAlign: 'right' }}>Horas</th>
                  <th style={{ textAlign: 'right' }}>Data/hora</th>
                  <th>Descrição</th>
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 && (
                  <tr>
                    <td colSpan={4}>
                      <div className="state-empty">
                        Nenhum apontamento de horas ainda. Use “+ Novo Apontamento” para registrar.
                      </div>
                    </td>
                  </tr>
                )}
                {entries.map((t) => (
                  <tr key={t.id}>
                    <td style={{ fontWeight: 500 }}>{t.employeeName}</td>
                    <td className="so-num">{formatHours(t.hours)}</td>
                    <td className="so-num">{formatDateTime(t.occurredAt)}</td>
                    <td>{t.description ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {entries.length > 0 && (
            <div className="so-table-footer">
              <span className="so-page-info">Total: <strong>{formatHours(sumHours)}</strong></span>
            </div>
          )}
        </Card>
      )}

      {/* ── Modal de novo apontamento ── */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={`Novo Apontamento — ${detail?.number ?? ''}`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleSubmit} loading={addTimeEntry.isPending}>Registrar apontamento</Button>
          </>
        }
      >
        <div className="so-form">
          <Input
            label="Colaborador *"
            value={form.employeeName}
            onChange={(e) => setForm((p) => ({ ...p, employeeName: e.target.value }))}
            placeholder="Nome de quem executou o serviço"
          />
          <Input
            label="Horas apontadas *"
            type="number"
            min="0.01"
            step="0.01"
            value={form.hours}
            onChange={(e) => setForm((p) => ({ ...p, hours: e.target.value }))}
            placeholder="Ex: 4.5"
          />
          <Input
            label="Data/hora do apontamento"
            type="datetime-local"
            value={form.occurredAt}
            onChange={(e) => setForm((p) => ({ ...p, occurredAt: e.target.value }))}
          />
          <Textarea
            label="Descrição do serviço executado"
            value={form.description}
            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            placeholder="Opcional — descreva o que foi feito"
          />
        </div>
      </Modal>

      <style>{`
        .so-page { padding: 1.5rem; }
        .so-kpi { background: #ffffff; border: 1px solid #c4c6cf; border-radius: 8px; padding: 1rem; box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04); }
        .so-kpi__top { display: flex; align-items: center; gap: 0.375rem; margin-bottom: 0.25rem; }
        .so-kpi__top .material-symbols-outlined { font-size: 18px; color: #43474e; }
        .so-table-wrap { overflow-x: auto; }
        .so-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .so-table th {
          padding: 0.75rem 1rem; background: #f2f4f6; color: #43474e; font-weight: 600;
          font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em;
          border-bottom: 2px solid #e0e3e5; text-align: left; white-space: nowrap; position: sticky; top: 0; z-index: 1;
        }
        .so-table td { padding: 0.75rem 1rem; border-bottom: 1px solid #eceef0; color: #191c1e; vertical-align: top; }
        .so-table tbody tr:hover td { background: #eceef0; }
        .so-num { text-align: right; font-family: 'JetBrains Mono', ui-monospace, monospace; font-variant-numeric: tabular-nums; white-space: nowrap; }
        .so-table-footer { display: flex; justify-content: flex-end; padding: 0.75rem 1rem; border-top: 1px solid #eceef0; }
        .so-page-info { font-size: 0.8125rem; color: #43474e; }
        .so-form { display: flex; flex-direction: column; gap: 1rem; min-width: 380px; }
        .state-error { text-align: center; }
        .state-error p { margin: 0; color: #ba1a1a; font-weight: 600; }
        .state-error .state-detail { color: #74777f; font-size: 0.875rem; font-weight: 400; margin-top: 0.5rem; }
        .state-empty { text-align: center; padding: 1.5rem 0.5rem; color: #74777f; }
      `}</style>
    </div>
  );
}